import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  runInInjectionContext,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormGroupDirective,
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatChipSelectionChange,
  MatChipsModule,
} from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PuebloBase } from '@components/pueblo-base.component';
import { EMPTY_INTEREST } from '@utils/defaults';
import { VIfDirective } from '@utils/v-if.directive';
import { Database as EmojiDatabase } from 'emoji-picker-element';
import { NativeEmoji } from 'emoji-picker-element/shared';
import { Interest, InterestResourceName, UserResourceName } from 'pueblo-types';
import {
  combineLatest,
  defer,
  distinctUntilChanged,
  first,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import {
  assertIsInterestSelectForm,
  IInterestSelectForm,
  InterestOption,
} from '../form-types';

@Component({
  selector: 'interest-select',
  templateUrl: 'interest-select.component.html',
  styleUrl: 'interest-select.component.scss',
  imports: [
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    VIfDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterestSelect extends PuebloBase implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly rootForm = inject(FormGroupDirective);

  private readonly emojiDatabase = new EmojiDatabase();

  readonly $showCountBadge = input(false, { alias: 'showCountBadge' });
  readonly $users = input<ReadonlyArray<UserResourceName>>([], {
    alias: 'users',
  });
  readonly $excludeInterests = input<ReadonlyArray<InterestResourceName>>([], {
    alias: 'excludeInterests',
  });
  readonly $lines = input(1, { alias: 'lines' });
  readonly $selectedLines = input(1, { alias: 'selectedLines' });

  protected readonly filterFormControl = this.fb.control('');
  protected readonly $interestsFilter = toSignal(
    this.filterFormControl.valueChanges,
    { initialValue: '' },
  );

  readonly $formPath = input.required<string[]>({ alias: 'formPath' });

  protected form!: FormGroup<IInterestSelectForm>;

  protected get selectedInterestsControl(): FormControl<
    ReadonlyArray<InterestOption>
  > {
    return this.form.controls.selectedInterests;
  }

  readonly interestSelected = output<InterestOption>();

  protected readonly $interestOptions = signal<InterestOption[]>([]);
  protected readonly $selectedInterestOptions = signal<InterestOption[]>([]);
  protected readonly $creatingInterest = signal(false);

  protected readonly $interestNames = toSignal(
    this.pbClient.getAllInterestNames(),
    { initialValue: [] },
  );

  protected readonly $showNewInterestChip = computed(() => {
    if (!this.$interestsFilter()) return false;
    return !this.$interestNames().find(
      (name) => name.name === this.$interestsFilter(),
    );
  });

  ngOnInit(): void {
    const form = this.rootForm.control.get(this.$formPath());
    assertIsInterestSelectForm(form);
    this.form = form;

    runInInjectionContext(this.injector, () => {
      combineLatest([
        this.pbClient.listAllInterests(),
        toObservable(this.$users),
        this.selectedInterestsControl.valueChanges.pipe(
          startWith(this.selectedInterestsControl.value),
        ),
        toObservable(this.$interestsFilter),
        toObservable(this.$excludeInterests),
      ])
        .pipe(
          map(
            ([
              allInterests,
              users,
              selectedInterests,
              interestsFilter,
              excludeInterests,
            ]) =>
              allInterests
                .filter((interest) => interest.name.startsWith(interestsFilter))
                .filter(
                  (interest) =>
                    !selectedInterests.some(
                      (option) =>
                        option.interest.resourceName === interest.resourceName,
                    ),
                )
                .filter(
                  (interest) =>
                    !excludeInterests.includes(interest.resourceName),
                )
                .map((interest) => ({
                  interest,
                  selected: selectedInterests.some(
                    (option) =>
                      option.interest.resourceName === interest.resourceName,
                  ),
                  userCount: users.length
                    ? users.filter((user) => interest.users.includes(user))
                        .length
                    : interest.users.length,
                }))
                .sort((a, b) => b.userCount - a.userCount),
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(this.$interestOptions.set);

      combineLatest([
        toObservable(this.$users),
        this.selectedInterestsControl.valueChanges.pipe(
          startWith(this.selectedInterestsControl.value),
        ),
      ])
        .pipe(
          map(([users, selectedInterests]) =>
            [...selectedInterests].map((option) => ({
              interest: option.interest,
              selected: selectedInterests.some(
                (option) =>
                  option.interest.resourceName === option.interest.resourceName,
              ),
              userCount: users.filter((user) =>
                option.interest.users.includes(user),
              ).length,
            })),
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(this.$selectedInterestOptions.set);
    });

    this.filterFormControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.filterFormControl.setValue(value.trim());
      });
  }

  protected handleInterestSelectionChange(
    change: MatChipSelectionChange,
    option: InterestOption,
  ): void {
    if (option.selected) {
      change.source.select();
    } else {
      change.source.deselect();
    }

    if (!change.isUserInput) return;
    this.filterFormControl.setValue('');
    if (change.selected) {
      this.selectInterestOption(option);
    } else {
      this.deselectInterestOption(option);
    }
  }

  protected async handleNewInterestClick() {
    this.$creatingInterest.set(true);
    defer(() =>
      this.emojiDatabase.getEmojiBySearchQuery(this.$interestsFilter()),
    )
      .pipe(
        switchMap((emojiResults) => {
          if (emojiResults.length) {
            const emoji = emojiResults.filter((result): result is NativeEmoji =>
              result.hasOwnProperty('unicode'),
            )[0];
            return of(emoji.unicode);
          }
          return this.pbClient.generateEmoji(this.$interestsFilter());
        }),
        first(),
      )
      .subscribe((emoji) => {
        const interest: Interest = {
          ...EMPTY_INTEREST,
          name: this.$interestsFilter(),
          emoji,
          users: [],
        };

        this.httpStore.createInterest(interest, {
          onSuccess: (interest: Interest) => {
            this.selectInterestOption({
              interest,
              selected: true,
              userCount: 1,
            });
            this.$creatingInterest.set(false);
          },
          onFailure: () => {
            this.$creatingInterest.set(false);
          },
        });
      });
  }

  protected isOptionSelected(option: InterestOption): boolean {
    return option.selected;
  }

  protected selectInterestOption(option: InterestOption): void {
    this.selectedInterestsControl.setValue([
      option,
      ...this.selectedInterestsControl.value.filter(
        (selectedOption) =>
          selectedOption.interest.resourceName !== option.interest.resourceName,
      ),
    ]);
    this.interestSelected.emit(option);
  }

  protected deselectInterestOption(option: InterestOption): void {
    this.selectedInterestsControl.setValue(
      this.selectedInterestsControl.value.filter(
        (selectedOption) =>
          selectedOption.interest.resourceName !== option.interest.resourceName,
      ),
    );
  }
}
